import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

/**
 * @module Date方法扩展
 */
import { isFunction } from './is';
/**
 * 以毫秒表示的周、日、时、分、秒常量
 * @ignore
 *
 */

export var SECOND_IN_MILLISECONDS = 1000;
export var MINUTE_IN_MILLISECONDS = SECOND_IN_MILLISECONDS * 60;
export var HOUR_IN_MILLISECONDS = MINUTE_IN_MILLISECONDS * 60;
export var DAY_IN_MILLISECONDS = HOUR_IN_MILLISECONDS * 24;
export var WEEK_IN_MILLISECONDS = DAY_IN_MILLISECONDS * 7;

var isDate = function isDate(date) {
  return date instanceof Date;
}; // 将一个日期转换为0h0m0s


function toDate(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
/**
 * 判断日期对象是否表示一个合法日期
 *
 * @export
 * @param {Date} date
 * @returns {bool} isValid 参数是否是一个合法的日期对象
 * @example
 *
 * isValid(new Date()) -> true
 *
 *
 */


export function isValid(date) {
  return Object.prototype.toString.call(date) === '[object Date]' && !Number.isNaN(date.getTime());
}
/**
 * 获取指定年月所含的天数
 *
 * @export
 * @param {number} year
 * @param {number} month 从0开始计数
 * @returns {number} 获取指定年月所含的天数
 *
 * daysInMonth(2020, 0) -> 31   2020年1月份天数
 * daysInMonth(2020, 1) -> 29   2020年2月份天数
 *
 */

export function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
/**
 * 将 Date 转化为指定格式的String
 *
 * @export
 * @param {Date} date 需要格式化的日期对象
 * @param {string} format 字符串表示的日期格式
 * @returns {string} formattedString 经过格式化的日期字符串
 *
 * @example
 *
 * formatDate(new Date('2020-01-01 18:30:40'), 'YYYY-MM-DD') -> '2020-01-01'
 * formatDate(new Date('2020-01-01 18:30:40'), 'YY-M-D') -> '20-1-1'
 * formatDate(new Date('2020-01-01 18:30:40'), 'YYYY-MM-DD hh:mm:ss') -> '2020-01-01 18:30:40'
 * formatDate(new Date('2020-01-01 18:03:04'), 'YYYY-MM-DD hh:mm:ss') -> '2020-01-01 18:03:04'
 * formatDate(new Date('2020-01-01 18:03:04'), 'YYYY-MM-DD h:m:s') -> '2020-01-01 18:3:4'
 *
 */

export function formatDate(date, format) {
  if (isFunction(format)) {
    return format(date);
  }

  var str = format;
  var o = {
    'M+': date.getMonth() + 1,
    'D+': date.getDate(),
    'h+': date.getHours(),
    'm+': date.getMinutes(),
    's+': date.getSeconds()
  };

  if (/(Y+)/.test(format)) {
    str = str.replace(RegExp.$1, date.getFullYear().toString().substr(4 - RegExp.$1.length));
  }

  for (var k in o) {
    // eslint-disable-line
    if (new RegExp("(".concat(k, ")")).test(format)) {
      str = str.replace(RegExp.$1, RegExp.$1.length === 1 ? o[k] : "00".concat(o[k]).substr(o[k].toString().length));
    }
  }

  return str;
}
/**
 * 将 2020-06-18 格式的字符串转化为Date
 *
 * @export
 * @param {string} formattedString 经过格式化的日期字符串
 * @returns {Date} date 日期对象
 * @example parseDate('2020-01-01').toLocaleString() -> "2020/1/1 上午12:00:00"
 * @example parseDate('2020-04-20').toLocaleString() -> "2020/4/20 上午12:00:00"
 */

export function parseDate(string) {
  var _string$split$map = string.split('-').map(function (x) {
    return parseInt(x, 10);
  }),
      _string$split$map2 = _slicedToArray(_string$split$map, 3),
      y = _string$split$map2[0],
      m = _string$split$map2[1],
      d = _string$split$map2[2];

  return new Date(y, m - 1, d);
}
/**
 * 将 2020-06-18 12:13:14格式的字符串转化为Date
 *
 * @export
 * @param {string} formattedString 经过格式化的时间字符串
 * @returns {Date} date 日期对象
 * @example parseTime('2020-06-18-12-13-14').toLocaleString() -> "2020/4/8 下午12:13:14"
 * @example parseTime('2020-06-18-14-13-14').toLocaleString() -> "2020/4/8 下午2:13:14"
 */

export function parseTime(string) {
  var _string$split$map3 = string.split('-').map(function (x) {
    return parseInt(x, 10);
  }),
      _string$split$map4 = _slicedToArray(_string$split$map3, 6),
      Y = _string$split$map4[0],
      M = _string$split$map4[1],
      D = _string$split$map4[2],
      h = _string$split$map4[3],
      m = _string$split$map4[4],
      s = _string$split$map4[5];

  return new Date(Y, M - 1, D, h, m, s);
}
/**
 * 获取给定日期的年周次
 *
 * @export
 * @param {Date} date
 * @returns {[number, number]} 数组 [year, week]
 * @example getYearWeek(new Date('2020-01-01')) -> [2020, 1]
 * @example getYearWeek(new Date('2020-01-06')) -> [2020, 2]
 * @example getYearWeek(new Date('2020-01-12')) -> [2020, 2]
 */

export function getYearWeek(date) {
  date = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  var yearStart = new Date(date.getFullYear(), 0, 1);
  var weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return [yearStart.getFullYear(), weekNumber];
}
/**
 * 获取给定日期的周次
 *
 * @export
 * @param {Date} date
 * @returns {number} 给定日期的周次
 * @example getWeekNumber(new Date('2020-01-01')) -> 1
 * @example getWeekNumber(new Date('2020-01-06')) -> 2
 * @example getWeekNumber(new Date('2020-01-12')) -> 2
 */

export function getWeekNumber(date) {
  return getYearWeek(date)[1];
}
/**
 * 设置日期在指定周次同一星期的日期，同一个星期日期
 *
 * @export
 * @param {Date} 给定日期
 * @param {number} 需要设置的周次
 * @returns {date} 改变后的日期
 * @example setWeek('2020-10-01',1).toLocaleString() -> "2020/1/2 上午8:00:00"
 */

export function setWeek(date, weekNumber) {
  var newDate = new Date(date);
  var diff = getWeekNumber(newDate) - weekNumber;
  newDate.setDate(newDate.getDate() - diff * 7);
  return newDate;
}
/**
 * 获取当前月份所在季度
 *
 * @export
 * @param {Date}
 * @returns {number} 季度（1、2、3、4）
 * @example getQuarter(new Date('2020-01-01')) -> 1
 * @example getQuarter(new Date('2020-04-01')) -> 2
 * @example getQuarter(new Date('2020-07-01')) -> 3
 * @example getQuarter(new Date('2020-10-01')) -> 4
 */

export function getQuarter(date) {
  return Math.ceil((date.getMonth() + 1) / 3);
}
/**
 * 获取指定日期当周的第一天
 *
 * @export
 * @param {Date} date
 * @param {number} [weekStartsOn=1] 以周几作为一周的第一天，0~6分别表示周日、周一至周六
 * @returns {Date} 当周的第一天
 * @example getStartOfWeek(new Date('2020-09-02')).toLocaleDateString() -> "2020/8/31"
 * @example getStartOfWeek(new Date('2020-10-01')).toLocaleDateString() -> "2020/9/28"
 */

export function getStartOfWeek(date) {
  var weekStartsOn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  var newDate = new Date(date);
  var curDay = date.getDay();
  var diffDay = (curDay < weekStartsOn ? 7 : 0) + curDay - weekStartsOn;
  newDate.setDate(newDate.getDate() - diffDay);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}
/**
 * 获取指定日期当周的最后一天
 *
 * @export
 * @param {Date} date
 * @param {number} [weekStartsOn=1] 以周几作为一周的第一天，0~6分别表示周日、周一至周六
 * @returns {Date} 当周的最后一天
 * @example getEndOfWeek(new Date('2020-09-02')).toLocaleDateString() -> "2020/9/6"
 * @example getEndOfWeek(new Date('2020-10-01')).toLocaleDateString() -> "2020/10/4"
 *
 */

export function getEndOfWeek(date) {
  var weekStartsOn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  var newDate = new Date(date);
  var curDay = date.getDay();
  var diffDay = (curDay < weekStartsOn ? -7 : 0) + 6 - (curDay - weekStartsOn);
  newDate.setDate(newDate.getDate() + diffDay);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
}
/**
 * 获取指定日期当月的第一天
 *
 * @export
 * @param {Date} date
 * @returns {Date} 当月的第一天
 * @example getStartOfMonth(new Date('2020-02-15')).toLocaleDateString() -> "2020/2/1"
 * @example getStartOfMonth(new Date('2020-04-15')).toLocaleDateString() -> "2020/4/1"
 * @example getStartOfMonth(new Date('2020-09-15')).toLocaleDateString() -> "2020/9/1"
 *
 */

export function getStartOfMonth(date) {
  var newDate = new Date(date);
  newDate.setDate(1);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}
/**
 * 获取指定日期当月的最后一天
 *
 * @export
 * @param {Date} date
 * @returns {Date} 当月的最后一天
 * @example getEndOfMonth(new Date('2019-01-01')).toLocaleDateString() -> "2019/1/31"
 * @example getEndOfMonth(new Date('2019-02-01')).toLocaleDateString() -> "2019/2/28"
 * @example getEndOfMonth(new Date('2020-02-01')).toLocaleDateString() -> "2020/2/29"
 *
 */

export function getEndOfMonth(date) {
  var newDate = new Date(date);
  newDate.setMonth(date.getMonth() + 1, 0);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
}
/**
 * 获取指定日期当年的第一天
 *
 * @export
 * @param {Date} date
 * @returns {Date} 当年的第一天
 * @example getStartOfYear(new Date('2020-04-01')).toLocaleDateString() -> "2020/1/1"
 * @example getStartOfYear(new Date('2019-04-01')).toLocaleDateString() -> "2019/1/1"
 *
 */

export function getStartOfYear(date) {
  var newDate = new Date(date);
  newDate.setFullYear(date.getFullYear(), 0, 1);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}
/**
 * 获取指定日期当年的最后一天
 *
 * @export
 * @param {Date} date
 * @returns {Date} 当年的最后一天
 * @example getEndOfYear(new Date('2020-01-01')).toLocaleDateString() -> "2020/12/31"
 * @example getEndOfYear(new Date('2019-01-01')).toLocaleDateString() -> "2019/12/31"
 */

export function getEndOfYear(date) {
  var newDate = new Date(date);
  newDate.setFullYear(date.getFullYear() + 1, 0, 0);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
}
/**
 * 对日期进行加减操作，并返回新的Date对象
 *
 * @export
 * @param {Date} date
 * @param {number} amount 加减的数量
 * @param {string} unit 'year'/'month'/'week'/'day'/'hour'/'minute'/'second'
 * @returns {Date} 经过加减操作生成的新Date对象
 * @example addDate(new Date('2020-01-01'),1,'year').toLocaleDateString() -> "2021/1/1"
 * @example addDate(new Date('2020-01-31'),1,'month').toLocaleDateString() -> "2020/2/29"
 * @example addDate(new Date('2020-01-31'),1,'week').toLocaleDateString() -> "2020/2/7"
 * @example addDate(new Date('2020-01-31'),1,'day').toLocaleDateString() -> "2020/2/1"
 */

export function addDate(date, amount, unit) {
  var intAmount = amount > 0 ? Math.floor(+amount) : Math.ceil(+amount);

  switch (unit) {
    case 'year':
      return addDate(date, intAmount * 12, 'month');

    case 'month':
      {
        var newDate = new Date(date);
        newDate.setMonth(date.getMonth() + intAmount, 1);
        newDate.setDate(Math.min(date.getDate(), getEndOfMonth(newDate).getDate()));
        return newDate;
      }

    case 'week':
      return new Date(date.getTime() + WEEK_IN_MILLISECONDS * intAmount);

    case 'day':
      return new Date(date.getTime() + DAY_IN_MILLISECONDS * intAmount);

    case 'hour':
      return new Date(date.getTime() + HOUR_IN_MILLISECONDS * intAmount);

    case 'minute':
      return new Date(date.getTime() + MINUTE_IN_MILLISECONDS * intAmount);

    case 'second':
      return new Date(date.getTime() + SECOND_IN_MILLISECONDS * intAmount);

    default:
      return new Date(date.getTime() + intAmount);
  }
}
/**
 * 计算两个日期间隔的季度
 *
 * @export
 * @param {Date} startDate 起始日期
 * @param {Date} endDate 结束日期
 * @returns {number} 两个日期间隔的季度
 * @example diffInQuarter(new Date('2020-01-01 14:00:00'),new Date('2020-04-02 12:00:00')) -> 1
 * @example diffInQuarter(new Date('2020-01-01 14:00:00'),new Date('2020-10-02 12:00:00')) -> 3
 * @example diffInQuarter(new Date('2020-01-01 14:00:00'),new Date('2019-10-02 12:00:00')) -> -1
 * @example diffInQuarter(new Date('2020-01-01 14:00:00'),new Date('2021-10-02 12:00:00')) -> 7
 * @example diffInQuarter(new Date('2020-01-01 14:00:00'),new Date('2019-01-02 12:00:00')) -> -4
 *
 */

export function diffInQuarter(startDate, endDate) {
  return endDate.getFullYear() * 4 + getQuarter(endDate) - (startDate.getFullYear() * 4 + getQuarter(startDate));
}
/**
 * 计算两个日期间隔的月份
 *
 * @export
 * @param {Date} startDate 起始日期
 * @param {Date} endDate 结束日期
 * @returns {number} 两个日期间隔的月份
 * @example diffInMonth(new Date('2020-04-10 12:00:00'),new Date('2020-04-01 12:00:00')) -> 0
 * @example diffInMonth(new Date('2020-01-01 14:00:00'),new Date('2020-04-02 12:00:00')) -> 3
 *
 */

export function diffInMonth(startDate, endDate) {
  return endDate.getFullYear() * 12 + endDate.getMonth() - (startDate.getFullYear() * 12 + startDate.getMonth());
}
/**
 * 计算两个日期间隔的年
 *
 * @export
 * @param {Date} startDate 起始日期
 * @param {Date} endDate 结束日期
 * @returns {number} 两个日期间隔的季度
 */

export function diffInYear(startDate, endDate) {
  return endDate.getFullYear() - startDate.getFullYear();
}
/**
 * 计算两个日期间隔的天数
 *
 * @export
 * @param {Date} startDate 起始日期
 * @param {Date} endDate 结束日期
 * @returns {number} 两个日期间隔的天数
 * @example diffInDay(new Date('2020-04-01 14:00:00'),new Date('2020-04-02 12:00:00')) -> -1
 * @example diffInDay(new Date('2020-04-01 12:00:00'),new Date('2020-04-01 16:00:00')) -> 0
 * @example diffInDay(new Date('2020-04-10 12:00:00'),new Date('2020-04-01 12:00:00')) -> 9
 *
 */

export function diffInDay(startDate, endDate) {
  return (toDate(startDate) - toDate(endDate)) / DAY_IN_MILLISECONDS;
}
/**
 * 判断给定所有日期是否在同一个月内
 *
 * @export
 * @param {Date} dates
 * @returns {boolean} 是否在同一个月
 * @example isSameMonth(new Date('2020-01-01 14:00:00'),new Date('2020-04-02 12:00:00')) -> false
 * @example isSameMonth(new Date('2020-04-01 14:00:00'),new Date('2020-04-02 12:00:00')) -> true
 */

export function isSameMonth() {
  for (var _len = arguments.length, dates = new Array(_len), _key = 0; _key < _len; _key++) {
    dates[_key] = arguments[_key];
  }

  if (!dates.length) return true;
  if (!isDate(dates[0])) return false;
  var comparedYear = dates[0].getFullYear();
  var comparedMonth = dates[0].getMonth();
  return dates.every(function (date) {
    return isDate(date) && date.getFullYear() === comparedYear && date.getMonth() === comparedMonth;
  });
}
/**
 * 判断给定所有日期是否在同一天内
 *
 * @export
 * @param {Date} dates
 * @returns {boolean}
 * @example isSameDay(new Date('2020-01-01 14:00:00'),new Date('2020-01-01 12:00:00')) -> true
 * @example isSameDay(new Date('2020-01-01 14:00:00'),new Date('2020-01-02 12:00:00')) -> false
 *
 */

export function isSameDay() {
  for (var _len2 = arguments.length, dates = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    dates[_key2] = arguments[_key2];
  }

  if (!dates.length) return true;
  if (!isDate(dates[0])) return false;
  var comparedYear = dates[0].getFullYear();
  var comparedMonth = dates[0].getMonth();
  var comparedDate = dates[0].getDate();
  return dates.every(function (date) {
    return isDate(date) && date.getFullYear() === comparedYear && date.getMonth() === comparedMonth && date.getDate() === comparedDate;
  });
}
/**
 * 判断给定所有日期是否在同季度内
 *
 * @export
 * @param {Date} dates
 * @returns {boolean}
 * @example isSameQuarter(new Date('2020-01-01 14:00:00'),new Date('2020-01-02 12:00:00')) -> true
 * @example isSameQuarter(new Date('2020-01-01 14:00:00'),new Date('2020-04-02 12:00:00')) -> false
 */

export function isSameQuarter() {
  for (var _len3 = arguments.length, dates = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    dates[_key3] = arguments[_key3];
  }

  if (!dates.length) return true;
  if (!isDate(dates[0]) || !isDate(dates[1])) return false;
  var comparedYear = dates[0].getFullYear();
  var comparedQuarter = getQuarter(dates[0]);
  return dates[1].getFullYear() === comparedYear && getQuarter(dates[1]) === comparedQuarter;
}
/**
 * 判断给定所有日期是否在同一年内
 *
 * @export
 * @param {Date} dates
 * @returns {boolean}
 */

export function isSameYear() {
  for (var _len4 = arguments.length, dates = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
    dates[_key4] = arguments[_key4];
  }

  if (!dates.length) return true;
  if (!isDate(dates[0]) || !isDate(dates[1])) return false;
  var comparedYear = dates[0].getFullYear();
  return dates[1].getFullYear() === comparedYear;
}
/**
 * 判断给定日期在今天
 *
 * @export
 * @param {Date} date
 * @returns {boolean} 所给日期date是否是今日
 * @example isToday(new Date('2020-01-01')) -> false
 * @example isToday(new Date()) -> true
 */

export function isToday(date) {
  return isSameDay(date, new Date());
}
/**
 * 判断给定日期在[min, max]范围内
 *
 * @export
 * @param {Date} target
 * @param {Date} min
 * @param {Date} max
 * @returns {boolean} 是否在范围之内
 * @example inRange(new Date('2020-02-01'),new Date('2020-01-01'),new Date('2020-01-15')) -> false
 * @example inRange(new Date('2020-02-01'),new Date('2020-01-01'),new Date('2020-03-15')) -> true
 *
 */

export function inRange(target, min, max) {
  return target >= min && target <= max;
}
/**
 * 查找最近的两个大促月（11月、6月）
 *
 * @export
 * @param {Date} date
 * @returns {array} 二维数组
 * @example findPromoteMonth(new Date('2020-08-01')) -> [[2020,6],[2019,11]]
 * @example findPromoteMonth(new Date('2020-01-01')) -> [[2019,11],[2019,6]]
 */

export function findPromoteMonth() {
  var date = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : new Date();
  var selectDate = new Date(date);

  var getYears = function getYears(types) {
    switch (types) {
      case 'minus':
        return new Date(date).getFullYear() - 1;

      default:
        return new Date(date).getFullYear();
    }
  };

  var lastSix = new Date(date.getFullYear(), 5, 30, 23, 59, 59, 59);
  var lastElen = new Date(date.getFullYear(), 10, 30, 23, 59, 59, 59);

  if (selectDate < lastSix) {
    return [[getYears('minus'), 11], [getYears('minus'), 6]];
  }

  if (selectDate >= lastSix && selectDate < lastElen) {
    return [[getYears(''), 6], [getYears('minus'), 11]];
  }

  return [[getYears(''), 11], [getYears(''), 6]];
}
/**
 * 获取日期是星期几
 *
 * @export
 * @param {Date} date
 * @param {number} [weekStartsOn=1] 以周几作为一周的第一天，0~6分别表示周日、周一至周六
 * @returns {number} 星期几
 */

function getWeekDay(date) {
  var weekStartsOn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  return (date.getDay() - weekStartsOn + 7) % 7;
}
/**
 * 获取指定周的第一天
 *
 * @export
 * @param {number} year
 * @param {number} week
 * @param {number} [weekStartsOn=1] 以周几作为一周的第一天，0~6分别表示周日、周一至周六
 * @returns {Date} 当周的第一天
 */


export function getWeekStart(year, week) {
  var weekStartsOn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
  var yearStart = new Date(year, 0, 1); // 第一天

  var firstDayOfWeek1Diff; // 第一周的第一天

  var yearStartDay = getWeekDay(yearStart, weekStartsOn);

  if (yearStartDay > 3) {
    // 如果1年的第一天是周5、6、7，第一周第一天从下周1开始
    firstDayOfWeek1Diff = 7 - yearStartDay;
  } else {
    // 如果1年的第一天是周1、2、3、4，第一周第一天从这周开始
    firstDayOfWeek1Diff = -yearStartDay;
  }

  return addDate(yearStart, firstDayOfWeek1Diff + (week - 1) * 7, 'day');
}
/**
 * 获取指定周的最后一天
 *
 * @export
 * @param {number} year
 * @param {number} week
 * @param {number} [weekStartsOn=1] 以周几作为一周的第一天，0~6分别表示周日、周一至周六
 * @returns {Date} 当周的最后一天
 */

export function getWeekEnd(year, week) {
  var weekStartsOn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
  return addDate(getWeekStart(year, week, weekStartsOn), 6, 'day');
}
/**
 * 获取指定月的第一天
 *
 * @export
 * @param {number} year
 * @param {number} month
 * @returns {Date} 当月的第一天
 *
 */

export function getMonthStart(year, month) {
  return new Date(year, month - 1, 1, 0, 0, 0, 0);
}
/**
 * 获取指定月的最后一天
 *
 * @export
 * @param {number} year
 * @param {number} month
 * @returns {Date} 当月的最后一天
 *
 */

export function getMonthEnd(year, month) {
  return new Date(year, month, 0, 0, 0, 0, 0);
}
/**
 * 获取指定季度的第一天
 *
 * @export
 * @param {number} year
 * @param {number} quarter
 * @returns {Date} 当季度的第一天
 *
 */

export function getQuarterStart(year, quarter) {
  return getMonthStart(year, quarter * 3 - 2);
}
/**
 * 获取指定季度的最后一天
 *
 * @export
 * @param {number} year
 * @param {number} quarter
 * @returns {Date} 当季度的最后一天
 *
 */

export function getQuarterEnd(year, quarter) {
  return getMonthEnd(year, quarter * 3);
}
/**
 * 获取指定年的第一天
 *
 * @export
 * @param {number} year
 * @param {number} quarter
 * @returns {Date} 当年的第一天
 *
 */

export function getYearStart(year) {
  return getMonthStart(year, 1);
}
/**
 * 获取指定年的最后一天
 *
 * @export
 * @param {number} year
 * @param {number} quarter
 * @returns {Date} 当年的最后一天
 *
 */

export function getYearEnd(year) {
  return getMonthEnd(year, 12);
}
/**
 * 获取某天所在月进度
 *
 * @export
 * @param {Date} date
 * @returns {number} 月进度
 *
 */

export function monthProgress() {
  var date = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : new Date();
  var dateArr = [date.getFullYear(), date.getMonth() + 1];
  var monthStart = getMonthStart.apply(void 0, dateArr);
  var monthEnd = getMonthEnd.apply(void 0, dateArr);
  return (diffInDay(date, monthStart) + 1) / (diffInDay(monthEnd, monthStart) + 1);
}
/**
 * 获取某天所在季度进度
 *
 * @export
 * @param {Date} date
 * @returns {number} 季度进度
 *
 */

export function quarterProgress() {
  var date = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : new Date();
  var year = date.getFullYear();
  var curQuarter = getQuarter(date);
  var quarterStart = getQuarterStart(year, curQuarter);
  var quarterEnd = getQuarterEnd(year, curQuarter);
  return (diffInDay(date, quarterStart) + 1) / (diffInDay(quarterEnd, quarterStart) + 1);
}
/**
 * 获取某天所在年进度
 *
 * @export
 * @param {Date} date
 * @returns {number} 年进度
 *
 */

export function yearProgress() {
  var date = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : new Date();
  var year = date.getFullYear();
  var yearStart = getYearStart(year);
  var yearEnd = getYearEnd(year);
  return (diffInDay(yearStart, date) + 1) / (diffInDay(yearStart, yearEnd) + 1);
}